import React from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  SimpleGrid,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  ArrowsClockwise,
  CheckCircle,
  Gear,
  Info,
  Warning,
  WarningOctagon,
  XCircle,
} from 'phosphor-react';
import { GlassCard } from '../../../../components/glass/GlassCard';
import { Dica } from '../lucroFiscal/ImpostoCard';
import {
  IApuracaoSn,
  IDominioAnexo,
  IItemLista,
  IParametroSn,
  IParcela,
  IPendenciaResumo,
} from './api';
import {
  FONTES_RBT12,
  SITUACOES,
  TRIBUTOS,
  brl,
  cnpjFmt,
  dataBr,
  diverge,
  pct,
  variacao,
} from './regras';

const VERDE = '#10B981';
const VERMELHO = '#F43F5E';
const ROMANO: Record<number, string> = { 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V' };
const NOME_ANEXO: Record<number, string> = {
  1: 'Comércio',
  2: 'Indústria',
  3: 'Serviços',
  4: 'Serviços (sem CPP)',
  5: 'Serviços (Fator R)',
};
const NIVEL: Record<IPendenciaResumo['nivel'], { rotulo: string; cor: string; fundo: string }> = {
  bloqueio: { rotulo: 'Bloqueio', cor: '#B91C1C', fundo: '#FEF2F2' },
  atencao: { rotulo: 'Atenção', cor: '#B45309', fundo: '#FFFBEB' },
  info: { rotulo: 'Info', cor: '#1D4ED8', fundo: '#EFF6FF' },
};

/** Parâmetros do Simples da empresa (toggletip do card). */
export function ParametrosResumo({ p }: { p: IParametroSn | null }) {
  if (!p) {
    return (
      <Text fontSize="12px" color="#64748B">
        Sem parâmetros: vale o padrão (ISS e ICMS dentro do DAS, serviço pelo item da LC 116).
        Ajuste na aba Simples Nacional do cadastro da empresa.
      </Text>
    );
  }
  const linha = (r: string, v: React.ReactNode) => (
    <Flex justify="space-between" gap={3} fontSize="12px" py="2px">
      <Text color="#64748B">{r}</Text>
      <Text color="#0F172A" fontWeight={600} textAlign="right">
        {v}
      </Text>
    </Flex>
  );
  return (
    <Box>
      {linha(
        'ISS fixo (fora do DAS)',
        p.iss_fixo ? `Sim${p.valor_fixo_iss ? ` · ${brl(p.valor_fixo_iss)}` : ''}` : 'Não',
      )}
      {linha('ICMS fora do DAS', p.icms_fora_do_das ? 'Sim' : 'Não')}
      {linha(
        'Anexo padrão de serviço',
        p.anexo_padrao_servico ? `Anexo ${p.anexo_padrao_servico}` : 'Pelo item',
      )}
      {linha('Início de atividade', dataBr(p.inicio_atividade))}
      {linha('Filiais', p.filiais.length ? p.filiais.map(cnpjFmt).join(', ') : 'Nenhuma')}
      {p.atividades.length > 0 && (
        <Box mt={2}>
          <Text fontSize="11px" color="#94A3B8" fontWeight={700}>
            REGRAS POR ITEM LC 116
          </Text>
          {p.atividades.map(a => (
            <Text key={a.item} fontSize="12px" color="#0F172A">
              {a.item} → Anexo {a.anexo}
              {a.fator_r ? ' (Fator R)' : ''}
              {a.nota ? ` · ${a.nota}` : ''}
            </Text>
          ))}
        </Box>
      )}
      {p.observacao && (
        <Text fontSize="12px" color="#475569" mt={2}>
          {p.observacao}
        </Text>
      )}
      {p.atualizado_por_nome && (
        <Text fontSize="10.5px" color="#94A3B8" mt={2}>
          Alterado por {p.atualizado_por_nome}
        </Text>
      )}
    </Box>
  );
}

const soma = (xs: (number | null | undefined)[]) => xs.reduce<number>((a, v) => a + (v ?? 0), 0);

/** Memória de cálculo de uma parcela (balão do valor nosso). */
function MemoriaParcela({ p, rbt12 }: { p: IParcela; rbt12: number | null }) {
  return (
    <VStack align="start" spacing={0.5}>
      <Text fontWeight="bold">
        Anexo {ROMANO[p.anexo] ?? p.anexo} · {p.faixa}ª faixa
      </Text>
      <Text>RBT12: {brl(rbt12)}</Text>
      <Text>
        Alíquota nominal {pct(p.aliquota_nominal)} · parcela a deduzir {brl(p.parcela_deduzir)}
      </Text>
      <Text color="gray.300">
        Efetiva = (RBT12 × {pct(p.aliquota_nominal)} − {brl(p.parcela_deduzir)}) ÷ RBT12 ={' '}
        {pct(p.aliquota_efetiva, 4)}
      </Text>
      <Text>
        Receita {brl(p.receita)} × {pct(p.aliquota_efetiva, 4)} = <b>{brl(p.valor)}</b>
      </Text>
      {p.fora_do_das.length > 0 && (
        <Text color="#FCD34D">
          Fora do DAS: {p.fora_do_das.map(t => TRIBUTOS[t] ?? t).join(', ')} (ST, monofásico, ISS
          retido/fixo ou ICMS fora)
        </Text>
      )}
    </VStack>
  );
}

/** Tributo a tributo: nosso × Domínio. */
function TabelaTributos({
  nosso,
  dominio,
}: {
  nosso: Record<string, number>;
  dominio?: IDominioAnexo;
}) {
  const chaves = Object.keys(TRIBUTOS).filter(
    t => (nosso[t] ?? 0) > 0.004 || (dominio?.tributos?.[t] ?? 0) > 0.004,
  );
  if (!chaves.length) return null;
  return (
    <Box mt={1} pt={1} borderTop="1px solid rgba(255,255,255,0.15)" w="full">
      <Flex color="gray.400" fontWeight="bold">
        <Text flex={1}>Tributo</Text>
        <Text w="92px" textAlign="right">
          Nosso
        </Text>
        <Text w="92px" textAlign="right">
          Domínio
        </Text>
      </Flex>
      {chaves.map(t => {
        const n = nosso[t] ?? 0;
        const d = dominio?.tributos?.[t] ?? null;
        const difere = d !== null && Math.abs(n - d) > 0.05;
        return (
          <Flex key={t} color={difere ? '#FCA5A5' : undefined}>
            <Text flex={1}>{TRIBUTOS[t]}</Text>
            <Text w="92px" textAlign="right">
              {brl(n)}
            </Text>
            <Text w="92px" textAlign="right">
              {d === null ? '—' : brl(d)}
            </Text>
          </Flex>
        );
      })}
    </Box>
  );
}

function Linha({
  rotulo,
  nosso,
  dominio,
  erro,
  dicaNosso,
  dicaDominio,
}: {
  rotulo: string;
  nosso: string;
  dominio: string;
  erro?: boolean;
  dicaNosso?: React.ReactNode;
  dicaDominio?: React.ReactNode;
}) {
  const valor = (texto: string, dica?: React.ReactNode, forte?: boolean) => {
    const el = (
      <Text
        fontSize="12px"
        fontWeight={forte ? 800 : 600}
        color="#0F172A"
        textAlign="right"
        whiteSpace="nowrap"
        borderBottom={dica ? '1px dashed #94A3B8' : undefined}
        cursor={dica ? 'help' : undefined}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {texto}
      </Text>
    );
    return dica ? <Dica conteudo={dica}>{el}</Dica> : el;
  };
  return (
    <Flex align="center" gap={2}>
      <Text fontSize="11px" color="#94A3B8" w="62px" flex="none">
        {rotulo}
      </Text>
      <Box flex={1} display="flex" justifyContent="flex-end">
        {valor(nosso, dicaNosso, rotulo === 'DAS')}
      </Box>
      <Box flex={1} display="flex" justifyContent="flex-end">
        {valor(dominio, dicaDominio)}
      </Box>
      <Flex w="16px" justify="center" flex="none">
        {erro === true && <XCircle size={15} weight="fill" color={VERMELHO} />}
        {erro === false && <CheckCircle size={15} weight="fill" color={VERDE} />}
      </Flex>
    </Flex>
  );
}

/** Um anexo: nosso cálculo × Domínio, com a memória de cálculo no mouse. */
function AnexoCard({
  anexo,
  parcelas,
  dominio,
  ap,
}: {
  anexo: number;
  parcelas: IParcela[];
  dominio?: IDominioAnexo;
  ap: IApuracaoSn | null;
}) {
  const calculado = !!ap;
  const receita = soma(parcelas.map(p => p.receita));
  const valor = soma(parcelas.map(p => p.valor));
  const efetiva = receita ? valor / receita : null;
  const tributos: Record<string, number> = {};
  parcelas.forEach(p =>
    Object.entries(p.tributos).forEach(([t, v]) => {
      tributos[t] = (tributos[t] ?? 0) + (v ?? 0);
    }),
  );
  const temNosso = parcelas.length > 0;
  const textoNosso = (v: string) => {
    if (!calculado) return 'não calculado';
    return temNosso ? v : 'sem receita';
  };
  const erroDas = calculado && diverge(temNosso ? valor : 0, dominio?.valor ?? 0);
  const erroReceita = calculado && diverge(temNosso ? receita : 0, dominio?.receita ?? 0);

  const memoria = temNosso ? (
    <VStack align="start" spacing={2}>
      {parcelas.map((p, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <MemoriaParcela key={i} p={p} rbt12={ap?.rbt12 ?? null} />
      ))}
      <TabelaTributos nosso={tributos} dominio={dominio} />
    </VStack>
  ) : undefined;
  const memoriaDominio = dominio ? (
    <VStack align="start" spacing={0.5}>
      <Text fontWeight="bold">Domínio · Anexo {ROMANO[anexo] ?? anexo}</Text>
      <Text>
        Base {brl(dominio.receita)} × {pct(dominio.aliquota_efetiva, 4)} = {brl(dominio.valor)}
      </Text>
      {!!dominio.iss_retido && <Text>ISS retido: {brl(dominio.iss_retido)}</Text>}
      <TabelaTributos nosso={tributos} dominio={dominio} />
    </VStack>
  ) : undefined;

  return (
    <Box
      p="10px 12px"
      borderRadius="14px"
      border="1px solid"
      borderColor={erroDas ? 'rgba(244,63,94,0.3)' : 'rgba(15,23,42,0.07)'}
      bg={erroDas ? 'rgba(255,241,242,0.65)' : 'rgba(255,255,255,0.7)'}
    >
      <Flex align="baseline" justify="space-between" mb={1} gap={2}>
        <Text fontSize="12.5px" fontWeight={800} color="#0F172A">
          Anexo {ROMANO[anexo] ?? anexo}
          <Text as="span" fontWeight={500} color="#94A3B8">
            {' '}
            · {NOME_ANEXO[anexo] ?? ''}
          </Text>
        </Text>
      </Flex>
      <Flex gap={2} mb="2px">
        <Box w="62px" flex="none" />
        <Text flex={1} textAlign="right" fontSize="10px" color="#94A3B8" fontWeight={700}>
          NOSSO
        </Text>
        <Text flex={1} textAlign="right" fontSize="10px" color="#94A3B8" fontWeight={700}>
          DOMÍNIO
        </Text>
        <Box w="16px" flex="none" />
      </Flex>
      <VStack align="stretch" spacing="3px">
        <Linha
          rotulo="Receita"
          nosso={textoNosso(brl(receita))}
          dominio={dominio ? brl(dominio.receita) : '—'}
          erro={calculado ? erroReceita : undefined}
        />
        <Linha
          rotulo="Alíquota"
          nosso={textoNosso(pct(efetiva, 4))}
          dominio={dominio ? pct(dominio.aliquota_efetiva, 4) : '—'}
          dicaNosso={memoria}
        />
        <Linha
          rotulo="DAS"
          nosso={textoNosso(brl(valor))}
          dominio={dominio ? brl(dominio.valor) : '—'}
          erro={calculado ? erroDas : undefined}
          dicaNosso={memoria}
          dicaDominio={memoriaDominio}
        />
      </VStack>
    </Box>
  );
}

function Metrica({
  rotulo,
  valor,
  alerta,
  dica,
  destaque,
}: {
  rotulo: string;
  valor: string;
  alerta?: boolean;
  dica?: string;
  destaque?: boolean;
}) {
  return (
    <Tooltip label={dica} isDisabled={!dica} fontSize="11px" hasArrow openDelay={300}>
      <Box minW={0}>
        <Text fontSize="10.5px" color="#64748B" fontWeight={600} noOfLines={1}>
          {rotulo}
        </Text>
        <Text
          fontSize={destaque ? '15px' : '13.5px'}
          fontWeight={destaque ? 800 : 700}
          color={alerta ? '#B91C1C' : '#0F172A'}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
          noOfLines={1}
        >
          {valor}
        </Text>
      </Box>
    </Tooltip>
  );
}

function Pendencias({ itens, total }: { itens: IPendenciaResumo[]; total: number }) {
  if (!itens.length) return null;
  const bloqueia = itens.some(p => p.nivel === 'bloqueio');
  return (
    <Box
      mt={3}
      p="9px 12px"
      borderRadius="12px"
      bg={bloqueia ? '#FEF2F2' : '#FFFBEB'}
      border="1px solid"
      borderColor={bloqueia ? '#FECACA' : '#FDE68A'}
      role="note"
    >
      <Flex align="center" gap={2} mb={1}>
        {bloqueia ? (
          <WarningOctagon size={16} weight="fill" color="#DC2626" />
        ) : (
          <Warning size={16} weight="fill" color="#D97706" />
        )}
        <Text fontSize="12px" fontWeight={700} color={bloqueia ? '#991B1B' : '#92400E'}>
          {total} pendência{total > 1 ? 's' : ''} para aprovar
          {bloqueia
            ? ' — tem bloqueio: precisa corrigir'
            : ' — aceite com justificativa ou corrija'}
        </Text>
      </Flex>
      <VStack align="stretch" spacing={1} pl="24px">
        {itens.map(p => {
          const n = NIVEL[p.nivel];
          return (
            <Flex key={p.id} gap={2} align="baseline">
              <Text fontSize="10px" fontWeight={800} color={n.cor} w="58px" flex="none">
                {n.rotulo.toUpperCase()}
              </Text>
              <Text fontSize="11.5px" color="#334155" lineHeight="1.45">
                {p.mensagem}
                {p.documento && (
                  <Text as="span" color="#64748B">
                    {' '}
                    ({p.documento})
                  </Text>
                )}
              </Text>
            </Flex>
          );
        })}
        {total > itens.length && (
          <Text fontSize="11px" color="#64748B">
            … e mais {total - itens.length} em Conferir.
          </Text>
        )}
      </VStack>
    </Box>
  );
}

export function ApuracaoCard({
  item,
  selecionado,
  onSelecionar,
  onAbrir,
  onCalcular,
  calculando,
  primeiro,
}: {
  item: IItemLista;
  selecionado: boolean;
  onSelecionar: (v: boolean) => void;
  onAbrir: () => void;
  onCalcular: () => void;
  calculando?: boolean;
  primeiro?: boolean;
}) {
  const { empresa, apuracao: ap, situacao, parametro } = item;
  const parcelas = item.parcelas ?? [];
  const dominio = item.dominio ?? [];
  const s = SITUACOES[situacao];
  const varia = variacao(ap);
  const variaAlta = varia !== null && Math.abs(varia) > 0.3;
  const anexos = Array.from(
    new Set([...parcelas.map(p => p.anexo), ...dominio.map(d => d.anexo)]),
  ).sort((a, b) => a - b);
  const totalDominio = dominio.length ? soma(dominio.map(d => d.valor)) : null;
  const receitaDominio = dominio.length ? soma(dominio.map(d => d.receita)) : null;
  const dasNosso = ap?.valor_das ?? null;
  const valorDominio = ap?.valor_dominio ?? totalDominio;

  return (
    <GlassCard intensity="strong" p="14px 16px" data-tour={primeiro ? 'card' : undefined}>
      <Flex align="center" gap={3} wrap="wrap">
        <Checkbox
          isChecked={selecionado}
          onChange={e => onSelecionar(e.target.checked)}
          aria-label={`Selecionar ${empresa.nome}`}
          data-tour={primeiro ? 'selecao' : undefined}
        />
        <Box minW={0} flex={1}>
          <Text fontSize="15px" fontWeight={700} color="#0F172A" noOfLines={1}>
            {empresa.nome}
          </Text>
          <Flex gap={2} mt={1} wrap="wrap" align="center">
            <Badge variant="subtle" colorScheme="gray" fontSize="10px">
              ID {empresa.external_id ?? '—'}
            </Badge>
            <Badge variant="subtle" colorScheme="blue" fontSize="10px">
              {cnpjFmt(empresa.cnpj)}
            </Badge>
            {empresa.fiscal && (
              <Text fontSize="11px" color="#64748B">
                Resp.: {empresa.fiscal}
              </Text>
            )}
          </Flex>
        </Box>
        <Tooltip label={s.dica} fontSize="11px" hasArrow>
          <Badge
            px={2}
            py={1}
            borderRadius="8px"
            bg={`${s.cor}1A`}
            color={s.cor}
            fontSize="11px"
            data-tour={primeiro ? 'situacao' : undefined}
          >
            {s.rotulo}
          </Badge>
        </Tooltip>
        <Popover placement="bottom-end" isLazy>
          <PopoverTrigger>
            <IconButton
              aria-label="Parâmetros do Simples"
              icon={<Gear size={16} />}
              size="sm"
              variant="ghost"
              data-tour={primeiro ? 'parametros' : undefined}
            />
          </PopoverTrigger>
          <Portal>
            <PopoverContent w="340px">
              <PopoverArrow />
              <PopoverHeader fontSize="13px" fontWeight={700}>
                Parâmetros do Simples
              </PopoverHeader>
              <PopoverBody>
                <ParametrosResumo p={parametro} />
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
        <Button
          size="sm"
          variant="outline"
          borderRadius="10px"
          leftIcon={<ArrowsClockwise size={14} />}
          isLoading={calculando}
          onClick={onCalcular}
        >
          {ap ? 'Recalcular' : 'Calcular'}
        </Button>
        <Button size="sm" colorScheme="blue" borderRadius="10px" onClick={onAbrir}>
          Conferir
        </Button>
      </Flex>

      {/* Totais do mês */}
      <SimpleGrid
        mt={3}
        columns={{ base: 2, md: 4, xl: 7 }}
        spacing={3}
        p="10px 12px"
        borderRadius="12px"
        bg="rgba(248,250,252,0.8)"
        border="1px solid rgba(15,23,42,0.05)"
        data-tour={primeiro ? 'valores' : undefined}
      >
        <Metrica
          rotulo="Receita (nosso)"
          valor={ap ? brl(ap.receita_bruta) : '—'}
          alerta={!!ap && diverge(ap.receita_bruta, receitaDominio)}
          dica={receitaDominio !== null ? `Domínio: ${brl(receitaDominio)}` : undefined}
        />
        <Metrica rotulo="DAS (nosso)" valor={ap ? brl(dasNosso) : 'não calculado'} destaque />
        <Metrica
          rotulo="DAS Domínio"
          valor={valorDominio !== null ? brl(valorDominio) : 'sem apuração'}
          alerta={!!ap && diverge(dasNosso, valorDominio)}
          dica={
            ap && valorDominio !== null && dasNosso !== null
              ? `Diferença: ${brl(dasNosso - valorDominio)}`
              : 'Só referência (gabarito)'
          }
        />
        <Metrica
          rotulo="SERPRO (rascunho)"
          valor={brl(ap?.valor_serpro)}
          alerta={
            !!ap &&
            ap.valor_serpro !== null &&
            dasNosso !== null &&
            Math.abs(ap.valor_serpro - dasNosso) >= 0.01
          }
          dica="Valor que a Receita calculou no rascunho: qualquer centavo de diferença bloqueia"
        />
        <Metrica
          rotulo="Mês anterior"
          valor={brl(ap?.valor_mes_anterior)}
          alerta={variaAlta}
          dica={varia !== null ? `Variação de ${pct(varia, 1)} (alerta acima de 30%)` : undefined}
        />
        <Metrica
          rotulo="RBT12"
          valor={brl(ap?.rbt12)}
          alerta={ap?.rbt12_fonte === 'sem'}
          dica={ap?.rbt12_fonte ? (FONTES_RBT12[ap.rbt12_fonte] ?? ap.rbt12_fonte) : undefined}
        />
        <Metrica
          rotulo="Fator R"
          valor={ap?.fator_r !== null && ap?.fator_r !== undefined ? pct(ap.fator_r) : '—'}
          dica={ap?.folha12 ? `Folha 12 meses: ${brl(ap.folha12)}` : undefined}
        />
      </SimpleGrid>

      {/* Anexos: nosso × Domínio */}
      {anexos.length > 0 ? (
        <SimpleGrid mt={3} columns={{ base: 1, md: 2, xl: Math.min(anexos.length, 4) }} spacing={2}>
          {anexos.map(a => (
            <AnexoCard
              key={a}
              anexo={a}
              ap={ap}
              parcelas={parcelas.filter(p => p.anexo === a)}
              dominio={dominio.find(d => d.anexo === a)}
            />
          ))}
        </SimpleGrid>
      ) : (
        <Flex mt={3} align="center" gap={2} fontSize="12px" color="#64748B">
          <Info size={14} />
          <Text>
            {ap
              ? 'Sem receita no mês, nem no Domínio.'
              : 'Sem apuração no Domínio e ainda não calculado aqui.'}
          </Text>
        </Flex>
      )}

      <Pendencias itens={item.pendencias ?? []} total={ap?.pendencias_abertas ?? 0} />

      {ap?.erro && (
        <Text fontSize="12px" color="#B91C1C" mt={2}>
          {ap.erro}
        </Text>
      )}
      {ap?.numero_das && (
        <Text fontSize="11.5px" color="#475569" mt={2}>
          DAS {ap.numero_das} · {brl(ap.das_total)} · vence {dataBr(ap.das_vencimento)}
        </Text>
      )}
    </GlassCard>
  );
}
